'use client';

import { Check, Copy, Facebook, Mail, Share2 } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@ui/components/ui/button';
import {
  copyToClipboard,
  getEmailShareUrl,
  getFacebookShareUrl,
  getWhatsAppShareUrl,
  getXShareUrl,
  isWebShareSupported,
  shareViaWebAPI
} from '@ui/utils/shareUtils';

export interface ShareOptionsContentProps {
  profileUrl: string;
  userName: string;
  translations?: {
    shareVia?: string;
    copyLink?: string;
    shareViaWhatsApp?: string;
    shareViaEmail?: string;
    shareViaFacebook?: string;
    shareViaX?: string;
    moreOptions?: string;
    linkCopied?: string;
  };
  onClose?: () => void;
}

export function ShareOptionsContent({ profileUrl, userName, translations, onClose }: ShareOptionsContentProps) {
  const [copied, setCopied] = useState(false);

  const handleCopyLink = async () => {
    try {
      await copyToClipboard(profileUrl);
      setCopied(true);
      setTimeout(() => {
        setCopied(false);
        onClose?.();
      }, 1500);
    } catch (error) {
      console.error('Failed to copy link:', error);
    }
  };

  const handleWhatsAppShare = () => {
    const shareMessage = `Check out ${userName}'s profile on TopTopTime!`;
    const url = getWhatsAppShareUrl(shareMessage, profileUrl);
    window.open(url, '_blank', 'noopener,noreferrer');
    onClose?.();
  };

  const handleEmailShare = () => {
    const url = getEmailShareUrl(`${userName}'s Profile`, profileUrl);
    window.location.href = url;
    onClose?.();
  };

  const handleFacebookShare = () => {
    const url = getFacebookShareUrl(profileUrl);
    window.open(url, '_blank', 'noopener,noreferrer');
    onClose?.();
  };

  const handleXShare = () => {
    const shareMessage = `Check out ${userName}'s profile on TopTopTime!`;
    const url = getXShareUrl(shareMessage, profileUrl);
    window.open(url, '_blank', 'noopener,noreferrer');
    onClose?.();
  };

  const handleMoreOptions = async () => {
    try {
      await shareViaWebAPI({
        title: `${userName}'s Profile`,
        text: `Check out ${userName}'s profile on TopTopTime!`,
        url: profileUrl
      });
      onClose?.();
    } catch (error) {
      console.error('Failed to share:', error);
    }
  };

  const hasWebShare = isWebShareSupported();

  return (
    <div className="space-y-3">
      <Button variant="outline" className="w-full justify-start gap-3 h-12" onClick={handleCopyLink}>
        {copied ? (
          <>
            <Check className="w-5 h-5 text-green-600" />
            <span className="text-green-600">{translations?.linkCopied || 'Link copied!'}</span>
          </>
        ) : (
          <>
            <Copy className="w-5 h-5" />
            <span>{translations?.copyLink || 'Copy Link'}</span>
          </>
        )}
      </Button>

      <Button variant="outline" className="w-full justify-start gap-3 h-12" onClick={handleWhatsAppShare}>
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
        </svg>
        <span>{translations?.shareViaWhatsApp || 'WhatsApp'}</span>
      </Button>

      <Button variant="outline" className="w-full justify-start gap-3 h-12" onClick={handleFacebookShare}>
        <Facebook className="w-5 h-5" />
        <span>{translations?.shareViaFacebook || 'Facebook'}</span>
      </Button>

      <Button variant="outline" className="w-full justify-start gap-3 h-12" onClick={handleXShare}>
        <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
          <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
        </svg>
        <span>{translations?.shareViaX || 'X'}</span>
      </Button>

      <Button variant="outline" className="w-full justify-start gap-3 h-12" onClick={handleEmailShare}>
        <Mail className="w-5 h-5" />
        <span>{translations?.shareViaEmail || 'Email'}</span>
      </Button>

      {hasWebShare && (
        <Button variant="outline" className="w-full justify-start gap-3 h-12" onClick={handleMoreOptions}>
          <Share2 className="w-5 h-5" />
          <span>{translations?.moreOptions || 'More...'}</span>
        </Button>
      )}
    </div>
  );
}
